/**
 * useFinanceOverview — TanStack Query wrapper around
 * ``GET /api/v1/finance/overview?period={...}``.
 *
 * Empty-state contract (BE-side, May 2026): when no connector is
 * connected the BE returns 200 with ``cash_flow: []`` + ``dscr: null``
 * + ``burn_rate: null`` + ``data_completeness.coverage_pct: 0``. The
 * FE renders the "anteprima — collega un connector" empty state, NOT
 * a fake number.
 *
 * Cache: 5min staleTime (matches the BE Redis cache TTL). The
 * ``refresh`` mutation re-fetches with ``X-Refresh-Cache: 1`` so the
 * BE recomputes from scratch.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type BalanceSnapshotBody,
  type BalanceSnapshotResponse,
  type FinanceDeadlinesResponse,
  type FinanceOverview,
  type FinancePeriod,
  createBalanceSnapshot,
  getFinanceOverview,
  getFinanceUpcomingDeadlines,
} from "@ando/core";

export function financeOverviewQueryKey(period: FinancePeriod): readonly unknown[] {
  return ["workspace", "finance", "overview", period] as const;
}

export function financeDeadlinesQueryKey(
  horizonDays: number,
): readonly unknown[] {
  return ["workspace", "finance", "deadlines", horizonDays] as const;
}

const FINANCE_STALE_TIME_MS = 5 * 60_000;
// Deadlines move slower than cash-flow → 15 min stale window cuts polling.
const FINANCE_DEADLINES_STALE_TIME_MS = 15 * 60_000;

/** Shared overview query — hook + `/workspace/finance` route loader (intent preload). */
export const financeOverviewQueryOptions = (period: FinancePeriod = "13w") =>
  queryOptions({
    queryKey: financeOverviewQueryKey(period),
    queryFn: () => getFinanceOverview(period),
    staleTime: FINANCE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

export function useFinanceOverview(period: FinancePeriod = "13w") {
  return useQuery(financeOverviewQueryOptions(period));
}

/**
 * useFinanceDeadlines — TanStack Query wrapper around
 * ``GET /api/v1/finance/upcoming-deadlines``.
 *
 * Empty-state contract (BE-side): when no fiscal connector is connected
 * the BE returns ``items: []``. The UI renders a graceful placeholder,
 * never a fake schedule.
 */
export function useFinanceDeadlines(horizonDays = 30) {
  return useQuery<FinanceDeadlinesResponse>({
    queryKey: financeDeadlinesQueryKey(horizonDays),
    queryFn: () => getFinanceUpcomingDeadlines(horizonDays),
    staleTime: FINANCE_DEADLINES_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * Manual refresh mutation — sends ``X-Refresh-Cache: 1`` so the BE
 * bypasses the 5-min Redis cache and recomputes from the connector
 * aggregates. On success the cached query is replaced.
 */
export function useRefreshFinanceOverview(period: FinancePeriod = "13w") {
  const qc = useQueryClient();
  return useMutation<FinanceOverview, Error>({
    mutationFn: () => getFinanceOverview(period, { refresh: true }),
    onSuccess: (data) => {
      qc.setQueryData(financeOverviewQueryKey(period), data);
    },
  });
}

/**
 * Persist a user-supplied "Patrimonio netto" snapshot (Adeguati
 * Assetti). On success we invalidate every cached finance overview so
 * the new equity indicator renders on the next paint.
 */
export function useCreateBalanceSnapshot() {
  const qc = useQueryClient();
  return useMutation<BalanceSnapshotResponse, Error, BalanceSnapshotBody>({
    mutationFn: (body) => createBalanceSnapshot(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", "finance", "overview"] });
    },
  });
}
